import { useEffect, useState } from 'react';

/**
 * SOURCE DE VÉRITÉ UNIQUE de la fin de prévente.
 * Pour changer la deadline PARTOUT (bandeau accueil + bannières fiche produit + carte offre),
 * il suffit de modifier la seule ligne ci-dessous.
 *
 * Format : new Date('AAAA-MM-JJTHH:MM:SS') — heure locale (Dakar / GMT).
 * Exemple « prévente exclusive qui finit dans ~16h » depuis le 14/06 ~23h → 15/06 15:00.
 */
export const PREVENTE_DEADLINE = new Date('2026-06-15T15:00:00');

export interface PreventeRemaining {
  days: number;
  hours: number;
  minutes: number;
  expired: boolean;
}

/** Temps restant jusqu'à la deadline, calculé à un instant `now` (ms). */
export function getPreventeRemaining(now: number = Date.now()): PreventeRemaining {
  const diff = PREVENTE_DEADLINE.getTime() - now;
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, expired: true };
  return {
    days: Math.floor(diff / 86_400_000),
    hours: Math.floor((diff % 86_400_000) / 3_600_000),
    minutes: Math.floor((diff % 3_600_000) / 60_000),
    expired: false,
  };
}

/** Hook : compte à rebours réactif (mise à jour chaque minute). */
export function usePreventeCountdown(): PreventeRemaining {
  const [remaining, setRemaining] = useState<PreventeRemaining>(() => getPreventeRemaining());
  useEffect(() => {
    const id = setInterval(() => setRemaining(getPreventeRemaining()), 60_000);
    return () => clearInterval(id);
  }, []);
  return remaining;
}

/** Échéance formatée en français : « 15 juin 2026 ». */
export function formatPreventeDate(): string {
  return PREVENTE_DEADLINE.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Restant compact : « 16h 02m » ou « 2j 16h 02m » (ou « terminée »). */
export function formatPreventeLeft(r: PreventeRemaining): string {
  if (r.expired) return 'terminée';
  const mm = String(r.minutes).padStart(2, '0');
  return `${r.days > 0 ? `${r.days}j ` : ''}${r.hours}h ${mm}m`;
}


/** Temps restant vers une deadline arbitraire (utilisé par les bandeaux pilotés en admin). */
export function remainingTo(targetMs: number, now: number = Date.now()): PreventeRemaining {
  const diff = targetMs - now;
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, expired: true };
  return {
    days: Math.floor(diff / 86_400_000),
    hours: Math.floor((diff % 86_400_000) / 3_600_000),
    minutes: Math.floor((diff % 3_600_000) / 60_000),
    expired: false,
  };
}

/** Compte à rebours réactif vers une deadline donnée (repli sur PREVENTE_DEADLINE si absente). */
export function useCountdownTo(deadline?: string | Date | null): PreventeRemaining {
  const target = deadline ? new Date(deadline).getTime() : PREVENTE_DEADLINE.getTime();
  const [r, setR] = useState<PreventeRemaining>(() => remainingTo(target));
  useEffect(() => {
    setR(remainingTo(target));
    const id = setInterval(() => setR(remainingTo(target)), 60_000);
    return () => clearInterval(id);
  }, [target]);
  return r;
}
